/**
 * Library of utility functions and enumerations related to a11y as it pertains to forms. See JSDOCs below
 * for example usages.
 */
const A11yForms = (function () {
    const _LOG_COLOR = 'mediumaquamarine';

    const _ERROR_MESSAGES = {
        REQUIRED: 'Enter mandatory field',
        VALID_DATE: 'Enter a valid date',
        VALID_DATETIME: 'Enter a valid date and time',
        VALID_EMAIL: 'Enter a valid email address',
        VALID_FAX: 'Enter a valid fax number',
        VALID_PHONE: 'Enter a valid phone number',
        VALID_ZIP: 'Enter a valid zip code',
        VALID_NUMERIC: 'Enter a numeric value',
        VALID_NPITAX: 'Enter a valid format'
    };

    const _VALIDATIONS = {
        DATE: 'date',
        DATETIME: 'datetime',
        EMAIL: 'email',
        FAX: 'fax',
        PHONE: 'phone',
        REQUIRED: 'required',
        ZIP: 'zip',
        NPI:'npi',
        TAXID: 'taxid',
        CALENDARDATE: 'calendardate'
    };

    return {
        ERROR_MESSAGES: _ERROR_MESSAGES,
        VALIDATIONS: _VALIDATIONS,
        addRequiredIndicatorNote: _addRequiredIndicatorNote,
        adjustAutocomplete: _adjustAutocomplete,
        clearFormFieldValidation: _clearFormFieldValidation,
        fixRequiredFields: _fixRequiredFields,
        showFormFieldError: _showFormFieldError,
        supressHtml5ValidationTooltips: _supressHtml5ValidationTooltips,
        validateFormField: _validateFormField
    };

    /**
     * This function attempts to automatically add the "* Indicates required field" note to all screens/modals which contain required fields.
     *
     * NOTE: for modals, the dialog must have been converted to the new UX/A11y form fields in order for this to work - in particular, the
     *         form fields need to have an ah-label element with the ah-required class, e.g. <label class="ah-label ah-required">. In addition, for
     *         correct placement inside a modal, the modal must have been converted to the new UX style, which makes use of the CSS class
     *         "ah-modal-content", e.g. <div class="ah-modal-content">.
     * @param {object} containerSelector css selector for container you want to limit the search by
     */
    function _addRequiredIndicatorNote(containerSelector) {
        if (!containerSelector || containerSelector === '') {   // NOSONAR (complaining about usage of ===)
            DevUtils.logWarn('No containerSel passed in - this may have a negative impact on performance therefore execution of this function will not proceed', 'A11yForms.addRequiredIndicatorNote()');
            return;
        } else {
            _logDebug('addRequiredIndicatorNote("' + containerSelector + '")');
            containerSelector = containerSelector ? containerSelector + ' ' : '';
        }

        let displayContainer;
        let isModal = $('.ah-modal').is(':visible');

        if (isModal) {
            $(containerSelector + '.ah-required-note.is-modal').remove();
            displayContainer = $(containerSelector.trim() + '.ah-modal:visible');
        } else {
            $('.ah-required-note.full-screen').removeClass('is-visible no-member-banner');
            // get the first main screen container that is visible (there will only be one at a time)
            let displayContainers = $('#MvcMainBody, #MvcDbLoadCtrl, #MvcMainBodyBre, #MvcMainBodyConfig');
            $(displayContainers).each(function (_index, element) {
                if ($(element).is(":visible")) {
                    displayContainer = element;
                    return false;
                }

                return true;
            });
        }

        if (displayContainer && $(displayContainer).length > 0) {
            // tweak this in instances where legacy "required indicators" are not triggering the required indicator note
            const requiredIndicatorSelector = '.ah-required:visible, .requiredfields:visible, .asteriskRequired:visible, .UmValid_Default:visible';

            if ($(containerSelector).find(requiredIndicatorSelector).length > 0) {
                _logVerbose('found required field in container [' + containerSelector + ']');

                if (!isModal) {
                    // when there is no member summary banner, we need to adjust the position of the note... doing that with the 'no-member-banner' CSS class
                    $('.ah-required-note.full-screen').addClass('is-visible').addClass($('#gcSummaryBanner:visible').length === 0 ? 'no-member-banner' : '');
                } else {
                    $(displayContainer).find('.ah-modal-content').filter(':first').prepend('<div class="ah-required-note is-modal is-visible"></div>');
                }
            }
        }
    }

    /**
     * Adjust the Kendo autocomplete results div UP if the form field is found to have an error (otherwise it displays with a gap
     * between the input field and the results)
     * @param {object} input the jquery form field input
     */
    function _adjustAutocomplete(input) {
        setTimeout(function () {
            if ($(input).hasClass('ah-form-field-error')) {
                // needs to be in a timeout so it has a chance to render once the dropdown opens
                $(input).data("kendoAutoComplete").ul.closest(".k-animation-container").addClass('ah-autocomplete-results-error');
            } else {
                $(input).data("kendoAutoComplete").ul.closest(".k-animation-container").removeClass('ah-autocomplete-results-error');
            }
        });
    }

    /**
     * Clear error(s) on form field(s) that were set by calling A11yForms.validateFormField(). If no paremeters
     * are passed in, this will blindly clear all errors on the screen. If an inputId is passed in, then the
     * clearing will be targeted to that specific form input field.
     * @param {string} [inputId] OPTIONAL input ID of the form field to clear
     * @param {string} [parentSelector] OPTIONAL CSS selector of the parent container of the input field (pass this in if this is what actually get the error border)
     * @param {string} [containerId] OPTIONAL CSS selector of the container of the input field
     */
    function _clearFormFieldValidation(inputId, parentSelector, containerId) {

        if (!inputId) {
            // blindly wipe out all errors on a page...
            var containerSelId = !containerId ? '' : containerId;
            $('div, span, input, select, textarea', containerSelId).find('.ah-form-field-error').removeAttr('aria-describedby');
            $('div, span, input, select, textarea', containerSelId).removeClass('ah-form-field-error');
            $('label', containerSelId).removeClass('ah-label-error');
            $('.ah-label.ah-label-error', containerSelId).removeClass('ah-label-error');
            $('span.ah-error-text', containerSelId).remove();

            // attempt to also clear the alert banner (but only do it if it's displaying the generic message, which indicates it was created due to a form validation error)
            if ($('.ah-alert-banner .alert-msg').text() === A11yAlerts.ALERT_MSG.GENERIC_ERROR) {
                A11yAlerts.remove();
            }
        } else {
            // target error removal to specific field...
            let inputElem = $('#' + inputId);
            let labelElem = $('label[for=' + inputId + ']');
            let isCheckbox = $(inputElem).is('input[type="checkbox"]');

            if (inputElem !== null) {
                if (!isCheckbox) {
                    let errorElem = _getErrorElement(inputElem, parentSelector);
                    let errorTextElem = $('#' + inputId + ' + .ah-error-text');
                    if ($(errorTextElem).length === 0) {
                        errorTextElem = $('#' + inputId + '_error');
                    }
                    errorElem.removeAttr('aria-describedby');
                    errorElem.removeClass('ah-form-field-error');
                    errorTextElem.text('');
                    labelElem.removeClass('ah-label-error');
                } else {
                    let checkboxLabel = $('#' + inputId + ' + label');
                    $(checkboxLabel).removeClass('ah-checkbox-error');
                }
            }
        }
    }

    /**
     * Function to dynamically add the "required" attribute to form fields which are set as required using the
     * ah-required attribute on the associated label
     * @param {string} [inputSel] optional selector to use when searching for required form fields to decorate (if null, this function will look for all form fields)
     * @param {object} [containerSelector] optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _fixRequiredFields(inputSel, containerSelector) {
        containerSelector = containerSelector ? containerSelector + ' ' : '';
        let querySel = inputSel ? containerSelector + inputSel : containerSelector + 'input.ah-input:not([required]), ' + containerSelector + 'select.ah-input:not([required]), ' + containerSelector + 'textarea.ah-input:not([required]),' + containerSelector + 'input.ah-ta-input:not([required]),' + containerSelector + 'input[type="radio"]:not([required])';

        _logDebug('Finding required input elements using querySel [' + querySel + ']');

        // get all input fields on the screen which are not currently set to required
        const inputFields = document.querySelectorAll(querySel);

        // interate through all those fields, and find the associated labels which are marked ah-required, then set the required attr on those input fields
        for (const input of inputFields) {
            if (input.labels) {
                for (const label of input.labels) {
                    if (label.classList.contains('ah-required')) {
                        _logVerbose('Adding required attribute for label [' + label.textContent + '] and input id = [' + input.id + ']');
                        input.setAttribute('required', 'true');
                        if (input.getAttribute('data-role') === 'dropdownlist' && input.parentElement !== null && input.parentElement.classList.contains('k-dropdown')) {
                            input.parentElement.setAttribute('aria-required', 'true');
                            _fixRequiredForKendoDropDownList(input, label);
                        }
                    }
                }
            }
        }
    }

    /**
     * Function to dynamically prefix the kendoDropDownList instructions text with "required".
     */
    function _fixRequiredForKendoDropDownList(input, label) {
        let divInstructionsElem = $('#'+ input.id + '_instructions');
        if (divInstructionsElem !== null) {
            let instructionsText = divInstructionsElem.text();
            divInstructionsElem.text('required ' + instructionsText);
            _logVerbose('Adding required prefix for label [' + label.textContent + '] to = [' + input.id+'_instructions' + ']');
        }
    }

    /**
     * Function to "turn on" the error display for the specifid form field
     * @param {string} inputId input ID of the form field with the error
     * @param {string} errorText error message to display under the form field
     * @param {string} errorTextElemId element where the errorText is displayed (optional)
     * @param {string} [parentSelector] CSS selector of the parent container of the input field (pass this in if this is what should actually get the error border)
     * @param {string} [containerObjOrSel] optional CSS selector, or object (e.g. see validateDecisionNewSave), to retrict how the inputElem should be found (e.g. perhaps there are two input fields with the same ID on the screen)
     * @param {string} [index] optional value to provide a unique errorText span id.
     * @param {boolean} [doNotStealFocus] optional value to not set focus to first error element
     */
    function _showFormFieldError(inputId, errorText, errorTextElemId, parentSelector, containerObjOrSel, index, doNotStealFocus) {
        containerObjOrSel = containerObjOrSel ? containerObjOrSel : '';
        doNotStealFocus = doNotStealFocus ? doNotStealFocus : false;
        let inputElem = null;
        if (typeof containerObjOrSel === 'object') {
            inputElem = $('#' + inputId, containerObjOrSel);
        } else {
            inputElem = $(containerObjOrSel + ' #' + inputId + ':not(".dontSerialize")');
        }

        let errorElem = _getErrorElement(inputElem, parentSelector),
            errorTextElem = _getErrorTextElement(inputId, errorElem, errorTextElemId, containerObjOrSel, index),
            labelElem = _getAssociatedLabelElement(inputId, containerObjOrSel),
            isCheckbox = $(inputElem).is('input[type="checkbox"]:not(".dontSerialize")'),
            isDropcheck = $(inputElem).hasClass('ah-dropcheck') || $(inputElem).hasClass('ah-legacy-dropcheck');

        if (!isCheckbox) {
            if (isDropcheck) {
                $(errorElem).addClass('ah-form-field-error');
                let dropcheckInput = $(errorElem).find('.ah-dropcheck-text, input[type="text"]');
                $(dropcheckInput).attr('aria-describedby', $(errorTextElem).attr('id'));
                labelElem = $('label[for="' + $(dropcheckInput).attr('id') + '"]');
            } else {
                $(errorElem).addClass('ah-form-field-error').attr('aria-describedby', $(errorTextElem).attr('id'));
            }

            $(errorTextElem).text(errorText);
            $(labelElem).addClass('ah-label-error');

            // set focus on the first form field in the DOM with an error
            // we want this focus to steal focus from the alert banner if created, so delay by a number of milliseconds
            if (!doNotStealFocus) {
                let errorFormField = document.querySelector('.ah-form-field-error');
                if (errorFormField !== null && errorFormField.nodeName !== 'INPUT' && errorFormField.nodeName !== 'TEXTAREA' && errorFormField.nodeName !== 'SELECT' && !$(errorFormField).hasClass('k-dropdown')) {
                    errorFormField = errorFormField.querySelector('input,select');
                }
                A11yUtil.setFocus(errorFormField, 800);
            }
        } else {
            let checkboxLabel = $('#' + inputId + ' + label');
            $(checkboxLabel).addClass('ah-checkbox-error');
        }
    }

    /**
     * Prevent the HTML5 validation tooltips from showing up on fields
     * @param {string} inputSelector CSS selector for the <input> field(s) for which you want to suppress validation tooltips
     */
    function _supressHtml5ValidationTooltips(inputSelector) {
        let inputFields = document.querySelectorAll(inputSelector);
        inputFields.forEach(function (inputField) {
            inputField.addEventListener('invalid', function (event) {
                event.preventDefault();
            });
        });
    }

    function _isControlHidden(inputElem) {
        let isHidden = false;
        let elem = $(inputElem);

        // the input elem for Kendo dropdowns is actually hidden, so in this case, use the <input> element's parent as the element to check
        if (elem.attr('data-role') === 'dropdownlist') {
            let parentElem = elem.parent();
            if (parentElem.hasClass('k-dropdown')) {
                elem = parentElem;
            }
        }

        try {
            if (!elem.is(":visible")) {
                isHidden = true;
            }
        } catch (ex) {
            _logDebug('_isControlHidden() exception while checking for hidden element, returning false', ex);
        }

        return isHidden;
    }
    /**
     * Validates form fields. Returns true if form field is valid according to passed-in validation rules.
     * E.g. usage:
     *      isValid &= A11yForms.validateFormField('txtUserNameR', 'div');
            isValid &= A11yForms.validateFormField('txtEmailPassRecovery', 'div', [A11yForms.VALIDATIONS.REQUIRED, A11yForms.VALIDATIONS.EMAIL]);
     * @param {string} inputId id of the form input field to validate
     * @param {string} [parentSelector] CSS selector of the parent container of the input field (pass this in if this is what should actually get the error border)
     * @param {string[]} [validations=A11yForms.VALIDATIONS.REQUIRED] enumerated list of validations to perform from the A11yForms.VALIDATIONS enum
     * @param {string} [errorTextElemId] element where the errorText is displayed (optional)
     * @param {string} [containerSelector] optional CSS selector to retrict how the inputElem should be found (e.g. perhaps there are two input fields with the same ID on the screen)
     * @returns {boolean} true if form field is valid
     */
    function _validateFormField(inputId, parentSelector, validations, errorTextElemId, containerSelector) {    // NOSONAR: Complaining about the complexity
        containerSelector = containerSelector ? containerSelector + ' ' : '';

        let isValid = true;
        let errorText = '';
        let inputElem = $(containerSelector + '#' + inputId);

        const emailRegEx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const zipRegEx = /^((\d{5}\-\d{4})|(\d{5})|(\d{9}))$/;
        const phoneRegEx = /^\d{3}-\d{3}-\d{4}$/;
        const npi = /^\d{10}$/;
        const taxid = /^\d{9}$/;

        // convert "validations" to aray if passed in as string; set default validation to REQUIRED if not passed in at all
        if (validations && typeof validations === 'string') {
            validations = [validations];
        } else if (!validations) {
            validations = [this.VALIDATIONS.REQUIRED];
        } else {
            // do nothing (this is here for sonar)
        }

        if (_isControlHidden(inputElem)) {
            return true;
        }
        if (inputElem !== null) {
            let inputElemVal = $.trim(inputElem.val());

            for (let validation of validations) {
                switch (validation) {
                    case this.VALIDATIONS.REQUIRED:
                        if (_isEmpty(inputElem)) {
                            errorText = _ERROR_MESSAGES.REQUIRED;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.EMAIL:
                        if (inputElemVal !== '' && !emailRegEx.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_EMAIL;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.DATE:
                        if (!_isValidDate(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_DATE;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.DATETIME:
                        if (!_isValidDateTime(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_DATETIME;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.PHONE:
                        if (inputElemVal !== '' && !phoneRegEx.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_PHONE;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.FAX:
                        if (inputElemVal !== '' && !phoneRegEx.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_FAX;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.ZIP:
                        if (inputElemVal !== '' && !zipRegEx.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_ZIP;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.NPI:
                        if (inputElemVal !== '' && !npi.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_NPITAX;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.TAXID:
                        if (inputElemVal !== '' && !taxid.test(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_NPITAX;
                            isValid = false;
                        }
                        break;
                    case this.VALIDATIONS.CALENDARDATE:
                        if (!_isValidCalendarDate(inputElemVal)) {
                            errorText = _ERROR_MESSAGES.VALID_DATE;
                            isValid = false;
                        }
                        break;
                    default:
                        _logDebug('[' + inputId + '] invalid validation=[' + validation + ']');
                        break;
                }

                if (!isValid) {
                    break;
                }
            }

            _toggleFormFieldError(isValid, inputId, errorText, errorTextElemId, parentSelector, containerSelector);
        }

        return isValid;
    }

    function _isEmpty(inputElem) {
        let isEmpty = false;
        let inputElemVal = $.trim(inputElem.val());
        let isSelect = $(inputElem).is('select');
        let isCheckbox = $(inputElem).is('input[type="checkbox"]');
        let isDropcheck = $(inputElem).hasClass('ah-dropcheck');

        isEmpty = (isSelect && inputElemVal === '-1') || (isCheckbox && !$(inputElem).is(':checked')) || (isDropcheck && $(inputElem).find('.ah-dropcheck-input-hdn').val() === '') || inputElemVal === '';

        return isEmpty;
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _getErrorElement(inputElem, parentSelector) {
        let parentElem;

        if ($(inputElem).parent().hasClass('k-dropdown')) {
            parentElem = inputElem.parent('.k-dropdown');
        } else if ($(inputElem).parent().parent().hasClass('k-datepicker')) {
            parentElem = inputElem.closest('.k-widget');
        } else {
            parentElem = null;
        }

        if (parentSelector) {
            parentElem = inputElem.parent(parentSelector);
        }

        return parentElem ? parentElem : inputElem;
    }

    function _getErrorTextElement(inputId, inputElem, errorTextElemId, containerObjOrSel, index) {
        let _getErrorTextObjFromContainer = function () {
            let obj;
            let objId = inputId + ((index === null || index === undefined) ? '' : index);   // Add the index to the ID (if provided)

            if (typeof containerObjOrSel === 'object') {
                obj = $('#' + objId + ' + .ah-error-text', containerObjOrSel);
                if (!obj || obj.length === 0) {
                    obj = $('#' + objId + '_error', containerObjOrSel);
                }
            } else if (containerObjOrSel && containerObjOrSel !== '') {
                obj = $(containerObjOrSel + ' #' + objId + ' + .ah-error-text');
                if (!obj || obj.length === 0) {
                    obj = $(containerObjOrSel + ' #' + objId + '_error');
                }
            } else {
                obj = $('#' + objId + '_error');
            }

            return obj;
        }

        let errorElem = inputElem;

        if (!(errorTextElemId === null || errorTextElemId === undefined || errorTextElemId === '')) {
            inputId = errorTextElemId;
            errorElem = $('#' + errorTextElemId);
        }

        let errorTextElem = _getErrorTextObjFromContainer();

        let isCheckbox = $(inputElem).is('input[type="checkbox"]:not(".dontSerialize")');
        let spanErrorId = inputId + ((index === null || index === undefined) ? '' : index) + '_error';

        if (!isCheckbox && $(errorTextElem).length === 0) {
            // create error text elem if one doesn't already exist
            $(errorElem).after('<span id="' + spanErrorId + '" class="ah-error-text"></span>');
            errorTextElem = _getErrorTextObjFromContainer();
        }
        return errorTextElem;
    }

    function _getAssociatedLabelElement(inputId, containerObjOrSel) {
        let labelElem = null;
        if (typeof containerObjOrSel === 'object') {
            labelElem = $('label[for=' + inputId + ']', containerObjOrSel);
        } else if (containerObjOrSel && containerObjOrSel !== '') {
            labelElem = $(containerObjOrSel + ' label[for=' + inputId + ']');
        } else {
            labelElem = $('label[for=' + inputId + ']');
        }

        return labelElem;
    }

    function _isValidDate(value) {
        return value === null || value === undefined || value === '' || IsDate(value);
    }
    function _isValidCalendarDate(value) {
        return value === null || value === undefined || value === '' ||( IsDate(value) && isValidCalendarDate(value));
    }

    function _isValidDateTime(value) {
        let isValid = true;

        try {
            let timestamp = new Date(Date.parse(value));
            isValid = !isNaN(timestamp) && IsDateTime(value);
        } catch (e) {
            isValid = false;
        }

        return isValid;
    }

    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yForms', object, _LOG_COLOR);
    }

    function _logVerbose(message, object) {
        DevUtils.logVerbose(message, 'A11yForms', object, _LOG_COLOR);
    }

    function _toggleFormFieldError(isValid, inputId, errorText, errorTextElemId, parentSelector, containerSelector) {
        if (!isValid) {
            _showFormFieldError(inputId, errorText, errorTextElemId, parentSelector, containerSelector);
        } else {
            _clearFormFieldValidation(inputId, parentSelector);
        }
    }
})();
